import React, { useState } from 'react'
import { Box, Button, CircularProgress, Container, CssBaseline, Paper, TextField, Typography } from '@mui/material'
import Env from '../../shared/config/envConfig'
import { useHistory } from 'react-router-dom'

interface Props {
  token: string | null
}

const SetPassword: React.FC<Props> = ({ token }) => {
  const history = useHistory()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async () => {
    setError(null)
    if (!newPassword || newPassword.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.')
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${Env.basePath}/api/user/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      })
      if (!response.ok) {
        const text = await response.text().catch(() => '')
        throw new Error(text || 'Fehler beim Setzen des Passworts. Der Link ist möglicherweise abgelaufen.')
      }
      setSuccess(true)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Container maxWidth='xs'>
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant='h6' color='error'>Ungültiger Link</Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            Dieser Link ist ungültig oder wurde bereits verwendet.
          </Typography>
        </Paper>
      </Container>
    )
  }

  if (success) {
    return (
      <Container maxWidth='xs'>
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant='h6' color='primary'>Passwort gesetzt!</Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            Dein Passwort wurde erfolgreich gesetzt. Du kannst dich jetzt einloggen.
          </Typography>
          <Button fullWidth variant='contained' sx={{ mt: 2 }} onClick={() => history.push('/login')}>
            Zum Login
          </Button>
        </Paper>
      </Container>
    )
  }

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant='h5' sx={{ mb: 2 }}>Passwort einrichten</Typography>
        <Box component='form' noValidate>
          <TextField
            margin='normal'
            required
            fullWidth
            label='Neues Passwort'
            type='password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
          />
          <TextField
            margin='normal'
            required
            fullWidth
            label='Passwort bestätigen'
            type='password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          />
          {error && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          <Button
            fullWidth
            variant='contained'
            color='primary'
            sx={{ mt: 2 }}
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : undefined}
          >
            {loading ? 'Wird gesetzt...' : 'Passwort setzen'}
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default SetPassword
